<template>
  <v-dialog max-width="720px" v-model="showFullDebitNoteDialog">
    <div class="modal-overlay">
      <div class="modal-container">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="icon-wrapper">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div>
              <h2>Create Debit Note</h2>
              <p class="header-subtitle">Full Reversal</p>
            </div>
          </div>
          <button class="close-btn" @click="closeDialog" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M18 6L6 18M6 6l12 12" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="modal-body">
          <p class="description">
            Review the sub-hire details below and provide remarks for creating this full reversal debit note.
          </p>

          <!-- Details Cards -->
          <div class="details-section">
            <div class="section-title">Sub-Hire Information</div>
            
            <div class="details-grid">
              <div class="detail-card">
                <label>Sub-Hire Number</label>
                <div class="value">{{ debitNoteData.sub_hire_id || '-' }}</div>
              </div>
              
              <div class="detail-card">
                <label>Status</label>
                <div class="value">
                  <span class="status-badge">{{ debitNoteData.status || '-' }}</span>
                </div>
              </div>
              
              <div class="detail-card full-width">
                <label>Vendor Name</label>
                <div class="value">{{ debitNoteData.transporter || '-' }}</div>
              </div>
            </div>

            <div class="section-title">Financial & Invoice Details</div>
            
            <div class="details-grid">
              <div class="detail-card">
                <label>Invoice Number</label>
                <div class="value">{{ debitNoteData.invoice_no || '-' }}</div>
              </div>
              
              <div class="detail-card amount-card">
                <label>Total Amount</label>
                <div class="value amount">{{ debitNoteData.all_legs_total_amount || '0.00' }}</div>
              </div>
              
              <div class="detail-card full-width">
                <label>Reason For Sub-Hire</label>
                <div class="value">{{ debitNoteData.reason_for_sub_hire || '-' }}</div>
              </div>
            </div>
          </div>

          <!-- Critical Alert -->
          <div class="alert-box critical">
            <div class="alert-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <div class="alert-content">
              <div class="alert-title">Critical Action - Full Reversal</div>
              <div class="alert-text">
                Creating this debit note will automatically reverse the sub-hire status to the previous state. You'll be able to update the sub-hire after correcting the data.
              </div>
            </div>
          </div>

          <!-- Remarks Field -->
          <div class="remarks-section">
            <InputField
              label="Remarks"
              required
              :rules="remarksRules"
              :clearable="true"
              placeholder="Provide a detailed reason for creating this debit note and reversing the cost..."
              hide-details="auto"
              v-model="remarks"
            />
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeDialog">
            Cancel
          </button>
          <button 
            class="btn btn-primary" 
            @click="confirmDebitNote"
            :disabled="!isFormValid"
          >
            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M5 13l4 4L19 7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Confirm & Create Debit Note
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import InputField from '@/components/FormBaseComponents/InputField.vue';

export default {
  components: {
    InputField
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    debitNoteData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    showFullDebitNoteDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    context() {
      return { parentComponent: this };
    },
    currentProfile() {
      let obj = JSON.parse(localStorage.getItem("userProfile"));
      if (!obj) {
        return null;
      } else {
        return obj;
      }
    },
    isFormValid() {
      return this.remarks && this.remarks.trim().length > 0;
    },
    remarksRules() {
      return [
        (val) => !!val || 'Remarks is required',
        (val) => (val && val.trim().length > 0) || 'Remarks cannot be empty'
      ];
    }
  },
  data() {
    return {
      remarks: null,
      payload: {}
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.remarks = null;
      }
    },
  },
  methods: {
    closeDialog() {
      this.showFullDebitNoteDialog = false;
    },

    confirmDebitNote() {
      this.$bus.$emit("showLoader", true);

      const payload = {
        subhire_id: this.debitNoteData.sub_hire_id,
        remarks: this.remarks,
      };

      this.$api.customerInvoice
        .DebitNoteAdd(payload)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Debit Note (Full Reversal) created successfully!",
            color: "success",
          });

          this.closeDialog();
          this.$emit("refreshList");
          this.$router.push(`/app/admin/invoice/debit-note/list`);
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: `${
              err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Couldn't create Debit Note"
            }`,
            color: "error",
          });
          this.formErrors = err.data;
        });
    }
  },
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.16), 0 12px 24px rgba(0, 0, 0, 0.12);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Header */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  gap: 16px;
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #dc4e06 0%, #ee7b08 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon {
  width: 24px;
  height: 24px;
  color: white;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}

.header-subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  color: #f07a03;
  font-weight: 600;
}

.close-btn {
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: #6b7280;
  transition: all 0.2s;
  flex-shrink: 0;
}

.close-btn:hover {
  background: #e5e7eb;
  color: #111827;
}

.close-btn svg {
  width: 20px;
  height: 20px;
  display: block;
}

/* Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.description {
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
}

/* Details Section */
.details-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.detail-card {
  background: #f9fafb;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
}

.detail-card:hover {
  border-color: #d1d5db;
  background: #ffffff;
}

.detail-card.full-width {
  grid-column: 1 / -1;
}

.detail-card label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.detail-card .value {
  font-size: 14px;
  color: #111827;
  font-weight: 600;
  word-break: break-word;
}

.value.amount {
  font-size: 18px;
  color: #e86608;
  font-family: 'Courier New', monospace;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.amount-card {
  background: linear-gradient(135deg, #0bedb8 0%, #07be59 100%);
  border-color: #16cd4a;
}

/* Alert Box */
.alert-box {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fcd34d;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 14px;
  margin-bottom: 24px;
}

.alert-box.critical {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 1.5px solid #fca5a5;
}

.alert-icon {
  width: 24px;
  height: 24px;
  color: #d97706;
  flex-shrink: 0;
}

.alert-box.critical .alert-icon {
  color: #ee860e;
}

.alert-icon svg {
  width: 100%;
  height: 100%;
}

.alert-content {
  flex: 1;
  min-width: 0;
}

.alert-title {
  font-size: 13px;
  font-weight: 600;
  color: #ea8108;
  margin-bottom: 4px;
}

.alert-box.critical .alert-title {
  color: #ee740a;
}

.alert-text {
  font-size: 13px;
  color: #e15808;
  line-height: 1.5;
}

.alert-box.critical .alert-text {
  color: #de7f11;
}

/* Remarks Section */
.remarks-section {
  margin-bottom: 0;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 16px 16px;
}

.btn {
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1.5px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.btn-primary {
  background: linear-gradient(135deg, #e9970a 0%, #bb4e0a 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #cb6a0a 0%, #c07b14 100%);
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: #fca5a5;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.btn-icon {
  width: 18px;
  height: 18px;
}

/* Responsive Design */
@media (max-width: 640px) {
  .modal-container {
    border-radius: 12px;
    max-height: 95vh;
  }

  .modal-header {
    padding: 20px;
  }

  .header-content {
    gap: 12px;
  }

  .icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .icon {
    width: 20px;
    height: 20px;
  }

  .modal-header h2 {
    font-size: 18px;
  }

  .modal-body {
    padding: 20px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .detail-card.full-width {
    grid-column: 1;
  }

  .modal-footer {
    flex-direction: column;
    padding: 16px 20px;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 8px;
  }

  .modal-header h2 {
    font-size: 16px;
  }

  .header-subtitle {
    font-size: 13px;
  }
}

/* Custom Scrollbar */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>